/**
 * One roster cell's chip: the shift type's name, colour and window.
 *
 * WHERE THE LABEL COMES FROM. Not from here, and not from any catalogue this
 * app holds. A shift type's name, colour and declared window are the tenant's
 * policy, and the platform does not serve policy — so the planner resolves them
 * onto each occurrence it answers and a board draws exactly what it was given.
 * That is why this takes an `OccurrenceDisplay` and not a shift-type code plus
 * a lookup: there is no lookup to hold.
 *
 * NO DISPLAY IS A REAL STATE. An item of a plan nobody has published has no
 * occurrence and therefore nothing resolved, so the chip falls back to the bare
 * code on a neutral ground. That is honest — the ward has not been told this
 * shift exists yet — and it is not a fabricated label.
 *
 * ONE CHIP, TWO BOARDS: Home's schedule card and the roster app's grid. They
 * drew the same thing from two copies before, which is two places for one
 * rendering rule to drift.
 */
import { useTranslation } from 'react-i18next';

import { localisedTitle } from '@/lib/localisedTitle';

/** A chip colour pair, token-backed. */
interface ChipStyle {
  bg: string;
  fg: string;
}

/**
 * A declared colour NAME to a CSS token family this app ships.
 *
 * THE ONE HARDCODED THING, and deliberately so: which families exist is a
 * property of this frontend's token set, while the colour CHOICE per shift type
 * comes from the declaration. DESIGN.md keeps one interactive accent, so these
 * are categorical status tones rather than "click me" signals.
 *
 * A name this app has no family for falls back to neutral — this app saying it
 * cannot draw a colour it was told, not the declaration being incomplete.
 */
const TOKEN_FAMILY: Record<string, ChipStyle> = {
  primary: { bg: 'var(--color-primary-100)', fg: 'var(--color-primary-700)' },
  // `sky` folds onto the single blue accent; a second blue would read as a
  // second interactive affordance.
  sky: { bg: 'var(--color-primary-50)', fg: 'var(--color-primary-700)' },
  indigo: { bg: 'var(--color-citation-100)', fg: 'var(--color-citation-700)' },
  amber: { bg: 'var(--color-warning-100)', fg: 'var(--color-warning-800)' },
  rose: { bg: 'var(--color-error-100)', fg: 'var(--color-error-700)' },
  neutral: { bg: 'var(--color-neutral-100)', fg: 'var(--color-neutral-700)' },
};

const NEUTRAL_CHIP: ChipStyle = TOKEN_FAMILY.neutral;

/**
 * What this chip draws, structurally.
 *
 * DECLARED HERE, NOT IMPORTED from the API layer. This is a widget: it takes
 * pixels-worth of data and renders it, and a widget that imports a type from
 * `api/endpoints` drags a transport concern into a library that should not know
 * one exists. `OccurrenceDisplay` satisfies this shape, so the planner's answer
 * still passes straight in — but anything else with a label and a colour does
 * too, which is the point.
 */
export interface ShiftChipDisplay {
  /** The name per locale, keyed by locale code. */
  label: Record<string, string>;
  /** A token name this app's token set renders. NEVER a CSS value. */
  colour: string;
  /** `HH:MM`. Both or neither. */
  start?: string;
  end?: string;
}

export interface ShiftChipProps {
  /** The shift-type code, drawn when nothing has been resolved for this cell. */
  code: string;
  /** What the planner resolved, when this cell has an occurrence. */
  display?: ShiftChipDisplay;
  /** Font size differs between the two boards; everything else does not. */
  size?: 'sm' | 'md';
}

export function ShiftChip({ code, display, size = 'sm' }: ShiftChipProps) {
  const { i18n } = useTranslation();
  // The overload returning `string | undefined`: an occurrence's label is the
  // tenant's own map and is not guaranteed to carry `en`.
  const label = localisedTitle(display?.label, i18n.language);
  const tone = (display && TOKEN_FAMILY[display.colour]) ?? NEUTRAL_CHIP;
  // Both or neither: a declared window is a pair, and half of one would read as
  // a shift that starts and never ends.
  const window = display?.start && display?.end ? `${display.start}–${display.end}` : null;
  return (
    <span
      className={`inline-flex min-h-7 items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold ${
        size === 'md' ? 'text-[13px] tracking-[-0.01em]' : 'text-[11.5px]'
      }`}
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      <span>{label ?? code}</span>
      {window && <span className="font-normal opacity-70">{window}</span>}
    </span>
  );
}
